import { FC, PointerEvent, useMemo, useRef, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import { IOccupiedSlot, ISlotPattern, TWeekday } from "../../../../entities/schedule";
import { STUDENT_ROUTE } from "../../../../app/router/routes";
import { SCHEDULE_SLOT_COUNT, SCHEDULE_WEEKDAYS } from "../../lib/constants";
import {
    getCellKey,
    getCellsFromPatterns,
    getPatternsFromCells,
    getSlotTimeLabel,
    IScheduleSlotCell,
} from "../../lib/slotPattern";
import { useMediaQuery } from "../../../../shared/lib/hooks/useMediaQuery";
import classes from './scheduleWeekGrid.module.scss'

interface IProps {
    value: ISlotPattern[];
    onChange?: (slots: ISlotPattern[]) => void;
    availabilitySlots?: ISlotPattern[];
    conflictSlots?: ISlotPattern[];
    occupiedSlots?: IOccupiedSlot[];
    editable?: boolean;
    disabledText?: string;
    allowUnavailableSelection?: boolean;
    variant?: 'availability' | 'regular';
    visibleWeekdays?: TWeekday[];
}

type TDragMode = 'select' | 'unselect'

interface IDragState {
    pointerId: number;
    mode: TDragMode;
    startCell: IScheduleSlotCell;
    baseCells: Set<string>;
    captureElement: HTMLElement;
}

const getCell = (weekday: TWeekday, slotIndex: number): IScheduleSlotCell => ({
    weekday,
    slotIndex,
})

const getCellsFromKeys = (keys: Set<string>): IScheduleSlotCell[] => {
    return Array.from(keys).map(cellKey => {
        const [weekday, slotIndex] = cellKey.split('-')

        return {
            weekday: weekday as TWeekday,
            slotIndex: Number(slotIndex),
        }
    })
}

export const ScheduleWeekGrid: FC<IProps> = ({
    value,
    onChange,
    availabilitySlots,
    conflictSlots = [],
    occupiedSlots = [],
    editable = false,
    disabledText = 'Недоступно',
    allowUnavailableSelection = false,
    variant = 'availability',
    visibleWeekdays,
}) => {

    const dragRef = useRef<IDragState | null>(null)
    const lastSelectionKeyRef = useRef<string>('')
    const [hoveredOccupiedKey, setHoveredOccupiedKey] = useState<string>('')
    const canHover = useMediaQuery('(hover: hover) and (pointer: fine)')

    const selectedCells = useMemo(() => getCellsFromPatterns(value), [value])
    const availableCells = useMemo(() => {
        return availabilitySlots ? getCellsFromPatterns(availabilitySlots) : null
    }, [availabilitySlots])
    const conflictCells = useMemo(() => getCellsFromPatterns(conflictSlots), [conflictSlots])
    const occupiedCells = useMemo(() => getCellsFromPatterns(occupiedSlots), [occupiedSlots])
    const occupiedSlotInfoByCellKey = useMemo(() => {
        return occupiedSlots.reduce((acc, occupiedSlot) => {
            for(
                let slotIndex = occupiedSlot.startSlot;
                slotIndex < occupiedSlot.startSlot + occupiedSlot.durationSlots;
                slotIndex++
            ){
                acc.set(getCellKey({weekday: occupiedSlot.weekday, slotIndex}), occupiedSlot)
            }

            return acc
        }, new Map<string, IOccupiedSlot>())
    }, [occupiedSlots])
    const weekdays = useMemo(() => {
        if(!visibleWeekdays?.length) return SCHEDULE_WEEKDAYS

        const visibleWeekdaysSet = new Set(visibleWeekdays)
        return SCHEDULE_WEEKDAYS.filter(weekday => visibleWeekdaysSet.has(weekday.value))
    }, [visibleWeekdays])

    const isCellAvailable = (cell: IScheduleSlotCell): boolean => {
        return availableCells ? availableCells.has(getCellKey(cell)) : true
    }

    const isCellSelectable = (cell: IScheduleSlotCell): boolean => {
        if(occupiedCells.has(getCellKey(cell))) return false

        return allowUnavailableSelection || isCellAvailable(cell)
    }

    const getCellFromPointer = (event: PointerEvent): IScheduleSlotCell | null => {
        const element = document.elementFromPoint(event.clientX, event.clientY)
        const slot = element?.closest<HTMLButtonElement>('[data-schedule-slot="true"]')
        const weekday = slot?.dataset.weekday
        const slotIndex = slot?.dataset.slotIndex

        if(!slot || !weekday || !slotIndex) return null

        return {
            weekday: weekday as TWeekday,
            slotIndex: Number(slotIndex),
        }
    }

    const getSelectableRange = (startCell: IScheduleSlotCell, endCell: IScheduleSlotCell): IScheduleSlotCell[] => {
        if(startCell.weekday !== endCell.weekday){
            return [startCell]
        }

        const direction = endCell.slotIndex >= startCell.slotIndex ? 1 : -1
        const range: IScheduleSlotCell[] = []

        for(
            let slotIndex = startCell.slotIndex;
            direction > 0 ? slotIndex <= endCell.slotIndex : slotIndex >= endCell.slotIndex;
            slotIndex += direction
        ){
            const cell = getCell(startCell.weekday, slotIndex)

            if(!isCellSelectable(cell)) break

            range.push(cell)
        }

        return range
    }

    const applySelection = (endCell: IScheduleSlotCell) => {
        const drag = dragRef.current

        if(!editable || !onChange || !drag) return

        const selectedRange = getSelectableRange(drag.startCell, endCell)
        const selectionKey = `${drag.mode}:${selectedRange.map(getCellKey).join(',')}`

        if(selectionKey === lastSelectionKeyRef.current) return

        const nextCells = new Set(drag.baseCells)

        selectedRange.forEach(cell => {
            const key = getCellKey(cell)

            if(drag.mode === 'select'){
                nextCells.add(key)
            }
            else{
                nextCells.delete(key)
            }
        })

        lastSelectionKeyRef.current = selectionKey
        onChange(getPatternsFromCells(getCellsFromKeys(nextCells)))
    }

    const releasePointerCapture = () => {
        const drag = dragRef.current

        if(drag?.captureElement.hasPointerCapture(drag.pointerId)){
            drag.captureElement.releasePointerCapture(drag.pointerId)
        }
    }

    const onPointerDown = (event: PointerEvent<HTMLButtonElement>, cell: IScheduleSlotCell) => {
        if(event.button !== 0 || !editable || !onChange || !isCellSelectable(cell)) return

        event.preventDefault()
        setHoveredOccupiedKey('')
        event.currentTarget.setPointerCapture(event.pointerId)

        const isSelected = selectedCells.has(getCellKey(cell))
        const mode: TDragMode = isSelected ? 'unselect' : 'select'

        dragRef.current = {
            pointerId: event.pointerId,
            mode,
            startCell: cell,
            baseCells: new Set(selectedCells),
            captureElement: event.currentTarget,
        }
        lastSelectionKeyRef.current = ''
        applySelection(cell)
    }

    const onPointerMove = (event: PointerEvent<HTMLElement>) => {
        const drag = dragRef.current

        if(!drag || drag.pointerId !== event.pointerId) return

        event.preventDefault()

        const cell = getCellFromPointer(event)

        if(!cell) return

        applySelection(cell)
    }

    const stopDrag = () => {
        releasePointerCapture()
        dragRef.current = null
        lastSelectionKeyRef.current = ''
    }

    const getOccupiedTitle = (occupiedSlot?: IOccupiedSlot): string => {
        if(!occupiedSlot) return 'Занято постоянным расписанием'

        const parts = [
            occupiedSlot.student?.fullName,
            occupiedSlot.tag?.name,
            occupiedSlot.parent,
        ].filter(Boolean)

        return parts.length
            ? `Занято постоянным расписанием · ${parts.join(' / ')}`
            : 'Занято постоянным расписанием'
    }

        return (
        <section
            className={
                `${classes.container} ${variant === 'regular' ? classes.regular : classes.availability}` +
                (editable ? ` ${classes.editable}` : '')
            }
            onPointerMove={onPointerMove}
            onPointerUp={stopDrag}
            onPointerCancel={stopDrag}
        >
            <table className={classes.table}>
                <thead>
                    <tr>
                        <th className={classes.timeHead} scope="col" aria-label="Время" />
                        {weekdays.map(weekday => (
                            <th key={weekday.value} scope="col">
                                <span className={classes.weekdayShort}>{weekday.shortLabel}</span>
                                <span className={classes.weekdayFull}>{weekday.label}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({length: SCHEDULE_SLOT_COUNT}, (_, slotIndex) => (
                        <tr key={slotIndex}>
                            <th
                                className={classes.timeCell}
                                scope="row"
                                data-time-label={slotIndex % 2 === 0 ? getSlotTimeLabel(slotIndex) : undefined}
                            />
                            {weekdays.map(weekday => {
                                const cell = getCell(weekday.value, slotIndex)
                                const key = getCellKey(cell)
                                const isSelected = selectedCells.has(key)
                                const isAvailable = isCellAvailable(cell)
                                const hasConflict = conflictCells.has(key)
                                const isOccupied = occupiedCells.has(key)
                                const occupiedSlotInfo = occupiedSlotInfoByCellKey.get(key)
                                const canOpenOccupiedCard = Boolean(
                                    occupiedSlotInfo?.student?.id ||
                                    occupiedSlotInfo?.student?.fullName ||
                                    occupiedSlotInfo?.tag?.name ||
                                    occupiedSlotInfo?.parent
                                )
                                const isOccupiedCardOpen = hoveredOccupiedKey === key

                                return (
                                    <td
                                        key={key}
                                        className={classes.slotCell +
                                            (isOccupiedCardOpen ? ` ${classes.occupiedCardOpen}` : '')
                                        }
                                        onMouseEnter={() => {
                                            if(canHover && canOpenOccupiedCard){
                                                setHoveredOccupiedKey(key)
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            if(canHover){
                                                setHoveredOccupiedKey(prev => prev === key ? '' : prev)
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            data-schedule-slot="true"
                                            data-weekday={weekday.value}
                                            data-slot-index={slotIndex}
                                            className={classes.slot +
                                                (isAvailable ? ` ${classes.available}` : ` ${classes.disabled}`) +
                                                (isSelected ? ` ${classes.selected}` : '') +
                                                (hasConflict ? ` ${classes.conflict}` : '') +
                                                (isOccupied ? ` ${classes.occupied}` : '')
                                            }
                                            aria-disabled={!isAvailable}
                                            title={
                                                `${weekday.label}, ${getSlotTimeLabel(slotIndex)}` +
                                                `${isOccupied ? ` · ${getOccupiedTitle(occupiedSlotInfo)}` : ''}` +
                                                `${!isAvailable ? ` · ${disabledText}` : ''}`
                                            }
                                            aria-expanded={isOccupied && canOpenOccupiedCard
                                                ? isOccupiedCardOpen
                                                : undefined
                                            }
                                            aria-haspopup={isOccupied && canOpenOccupiedCard ? 'dialog' : undefined}
                                            onPointerDown={event => onPointerDown(event, cell)}
                                            onClick={event => {
                                                if(!isOccupied || !canOpenOccupiedCard) return

                                                event.stopPropagation()
                                                setHoveredOccupiedKey(currentKey => (
                                                    currentKey === key ? '' : key
                                                ))
                                            }}
                                        />
                                        {isOccupiedCardOpen && occupiedSlotInfo && (
                                            <section
                                                className={classes.occupiedCard}
                                                role="dialog"
                                                aria-label="Информация о занятом слоте"
                                                onClick={event => event.stopPropagation()}
                                            >
                                                <section className={classes.occupiedCardTitle}>
                                                    Занято постоянным расписанием
                                                </section>
                                                {occupiedSlotInfo.student?.fullName && (
                                                    <section className={classes.occupiedCardLine}>
                                                        <span>Студент:</span>
                                                        <span>{occupiedSlotInfo.student.fullName}</span>
                                                    </section>
                                                )}
                                                {occupiedSlotInfo.tag?.name && (
                                                    <section className={classes.occupiedCardLine}>
                                                        <span>Тег:</span>
                                                        <span>{occupiedSlotInfo.tag.name}</span>
                                                    </section>
                                                )}
                                                {occupiedSlotInfo.parent && (
                                                    <section className={classes.occupiedCardLine}>
                                                        <span>Родитель:</span>
                                                        <span>{occupiedSlotInfo.parent}</span>
                                                    </section>
                                                )}
                                                {occupiedSlotInfo.student?.id && (
                                                    <Link
                                                        className={classes.occupiedCardLink}
                                                        to={generatePath(STUDENT_ROUTE.path, {id: occupiedSlotInfo.student.id})}
                                                    >
                                                        Перейти в карточку студента
                                                    </Link>
                                                )}
                                            </section>
                                        )}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th className={classes.timeEndCell} scope="row" data-time-label="24:00" />
                        {weekdays.map(weekday => (
                            <td key={weekday.value} />
                        ))}
                    </tr>
                </tfoot>
            </table>
        </section>
    )
}
